<template>
  <div
    class="fixed top-0 left-0 w-screen h-screen overflow-auto bg-slate-100 scrollbar-lg"
    :style="{ zIndex: 999 }"
  >
    <div class="sticky top-0 left-0" :style="{ zIndex: 49 }">
      <div
        class="flex flex-wrap items-center justify-between flex-shrink-0 h-10 px-6 lg:flex-no-wrap gap-y-2 gap-x-4 bg-pure-white"
      >
        <BlBreadcrumbs :breadcrumbs="breadcrumbs" />
      </div>
      <div
        class="flex items-center justify-between px-6 border-b h-14 bg-pure-white border-slate-400"
      >
        <div class="flex items-center gap-4">
          <BlTextTitle4 text="Create new recurring appointments" />
          <div class="flex items-center gap-4">
            <div
              class="flex items-center gap-1 px-1 rounded max-w-80 h-7 bg-tulip-100"
              :title="clientDataInput.name"
            >
              <BlIcon icon="user-square" color="tulip-7" class="w-6 h-6" />
              <BlTextSubtitle3
                :text="clientDataInput.name"
                class="truncate text-tulip-700"
              />
            </div>
            <div v-if="clientDataInput.id">
              <BlTextBody2
                v-if="availableCredits < 1"
                :text="`No available credit(s)`"
                class="text-tomato-700"
              />
              <BlTextBody2
                v-else
                :text="`${availableCredits} available credit(s)`"
                class="text-lime-700"
              />
            </div>
          </div>
        </div>
        <div class="flex items-center gap-4">
          <!-- <BlInputButton
            v-if="bookingStatus === 'in_progress'"
            type="outlined"
            label="Refresh"
            dense
            :loading="extendLoading"
            @clicked="onRefresh"
          /> -->
          <BlInputButton
            v-if="counter <= alertCountdown && bookingStatus === 'in_progress'"
            type="outlined"
            label="Extend by 5 minutes"
            :loading="extendLoading"
            @clicked="onExtendCountdown"
          />
          <div
            v-if="bookingStatus === 'in_progress'"
            class="flex items-center gap-2"
          >
            <BlIcon
              icon="timer"
              :color="counter <= alertCountdown ? 'tomato-7' : 'slate-6'"
              class="w-6 h-6"
            />
            <div v-if="extendLoading" class="px-4">
              <div class="blum-loader-btn"></div>
            </div>
            <BlTextTitle4
              v-else
              :text="countdown"
              :class="[
                counter <= alertCountdown ? 'text-tomato-700' : 'text-slate-700'
              ]"
            />
          </div>
          <BlInputButton
            type="plain"
            icon="close"
            :loading="closeLoading"
            @clicked="onClickClose"
          />
        </div>
      </div>
      <div
        class="absolute flex items-center justify-between w-full px-6 overflow-hidden transition-all duration-300 bg-tomato-700"
        :class="[showAlertCountdown ? 'h-9' : 'h-0']"
      >
        <div class="flex items-center gap-2">
          <BlIcon icon="timer" color="white" class="w-4 h-4" />
          <div>
            <BlTextBody2
              :text="`Please complete the appointment process within the next `"
              class="text-tomato-100"
            />
            <BlTextBody2
              :text="
                Math.ceil(counter / 60) +
                  ` minute${Math.ceil(counter / 60) > 1 ? 's' : ''} `
              "
              class="text-tomato-100"
            />
            <BlTextBody2
              :text="`to avoid losing your timeslot and having to start over.`"
              class="text-tomato-100"
            />
          </div>
        </div>
        <BlIcon
          icon="close"
          color="white"
          class="w-4 h-4"
          clickable
          @clicked="showAlertCountdown = false"
        />
      </div>
    </div>

    <div v-if="modalLoading" class="grid h-100 place-items-center">
      <div class="blum-loader"></div>
    </div>
    <div
      v-else-if="bookingStatus === 'expired'"
      class="flex items-center justify-center"
      :style="{ height: 'calc(100vh - 6rem - 2.5rem)' }"
    >
      <div class="flex flex-col items-center gap-3 text-center w-132">
        <BlIcon icon="timer" color="tomato-7" class="w-10 h-10" />
        <BlTextTitle4 text="Booking time out" class="text-tomato-700" />
        <BlTextBody2
          text="The time limit has been reached, and and the selected timeslot is no longer available. Please restart the booking process to secure a new timeslot."
          class="text-tomato-700"
        />
        <BlInputButton
          type="outlined-error"
          label="Close"
          :loading="closeLoading"
          @clicked="onClickClose"
        />
      </div>
    </div>
    <div
      v-else-if="bookingStatus === 'completed'"
      class="flex items-center justify-center"
      :style="{ height: 'calc(100vh - 6rem - 2.5rem)' }"
    >
      <div class="flex flex-col items-center gap-3 text-center w-132">
        <BlIcon icon="check" color="lime-6" class="w-32 h-32" />
        <BlTextTitle4 text="Booking completed" class="text-lime-700" />
      </div>
    </div>
    <div v-else>
      <SelectClientRecurring
        v-if="process === 'select_client' && currentSlot"
        :timeslotView="timeslotView"
        :timeslotViewOptions="timeslotViewOptions"
        @set-timeslot-view="timeslotView = $event"
        :availability="availability"
        :currentSlot="currentSlot"
        :client="clientDataInput"
        :clientAppointments="clientAppointments"
        :submitLoading="submitLoading"
        @set-client="onChangeClient"
        @continue="onCreateBooking"
      />

      <ManageScheduleRecurring
        v-if="process === 'manage_schedule'"
        :timeslotView="timeslotView"
        :timeslotViewOptions="timeslotViewOptions"
        @set-timeslot-view="timeslotView = $event"
        :clientAppointments="clientAppointments"
        :recurrence="recurrenceInput"
        @set-recurrence="onChangeRecurrance"
        @add-timeslot="process = 'add_timeslot'"
        @edit-timeslot="openEditTimeslot"
        @delete-timeslot="onDeleteTimeslot"
        @open-previous="previousOpened = true"
      />

      <HandleTimeslotRecurring
        v-if="process === 'add_timeslot'"
        :timeslotView="timeslotView"
        :timeslotViewOptions="timeslotViewOptions"
        @set-timeslot-view="timeslotView = $event"
        :client="clientDataInput"
        @cancel="process = 'manage_schedule'"
        @continue="process = 'manage_schedule'"
        @after-commit="fetchAvailbleCredits(clientDataInput.id)"
      />

      <HandleTimeslotRecurring
        v-if="process === 'edit_timeslot' && editTimeslot"
        :timeslotView="timeslotView"
        :timeslotViewOptions="timeslotViewOptions"
        @set-timeslot-view="timeslotView = $event"
        :client="clientDataInput"
        :timeslot="editTimeslot"
        editState
        @cancel="process = 'manage_schedule'"
        @continue="process = 'manage_schedule'"
        @after-commit="fetchAvailbleCredits(clientDataInput.id)"
      />

      <ReviewAppointmentRecurring
        v-if="process === 'review_appointment'"
        :timeslotView="timeslotView"
        :timeslotViewOptions="timeslotViewOptions"
        @set-timeslot-view="timeslotView = $event"
        :client="clientDataInput"
        :clientAppointments="clientAppointments"
        @set-client-appointments="clientAppointments = $event"
        @delete-timeslot="onDeleteReviewedTimeslot"
      />
    </div>

    <div
      v-if="
        bookingStatus === 'in_progress' &&
          (process === 'manage_schedule' || process === 'review_appointment')
      "
      class="sticky bottom-0 left-0 flex items-center justify-between flex-shrink-0 h-20 px-6 border-t bg-pure-white border-slate-400"
      :style="{ zIndex: 99 }"
    >
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-1">
          <div class="w-20 h-1 rounded bg-light-purple"></div>
          <div
            class="w-20 h-1 rounded"
            :class="[
              process === 'review_appointment'
                ? 'bg-light-purple'
                : 'bg-slate-400'
            ]"
          ></div>
        </div>
        <div
          v-if="process === 'manage_schedule'"
          class="flex items-center gap-1"
        >
          <div
            class="grid flex-shrink-0 w-6 h-6 border rounded-full place-items-center"
            :class="[
              isDisabledReview ? 'border-slate-600' : 'border-success-400'
            ]"
          >
            <BlTextSubtitle4 v-if="isDisabledReview" text="1" />
            <BlIcon v-else icon="check" color="success-4" class="w-6 h-6" />
          </div>
          <BlTextSubtitle4 text="Manage timeslots and set recurrence" />
        </div>
        <div
          v-if="process === 'review_appointment'"
          class="flex items-center gap-1"
        >
          <div
            class="grid flex-shrink-0 w-6 h-6 border rounded-full place-items-center"
            :class="[
              isDisabledSubmit ? 'border-slate-600' : 'border-success-400'
            ]"
          >
            <BlTextSubtitle4 v-if="isDisabledSubmit" text="2" />
            <BlIcon v-else icon="check" color="success-4" class="w-6 h-6" />
          </div>
          <BlTextSubtitle4 text="Review and confirm appointments" />
        </div>
      </div>
      <div>
        <BlInputButton
          v-if="process === 'manage_schedule'"
          label="Review all appointments"
          :disabled="isDisabledReview"
          :loading="reviewLoading"
          @clicked="onReview"
        />
        <div
          v-if="process === 'review_appointment'"
          class="flex items-center gap-4"
        >
          <BlInputButton
            type="outlined"
            label="Back"
            @clicked="backOpened = true"
          />
          <BlInputButton
            :label="`Book ${bookingTimeslots.length} appointment(s)`"
            :disabled="isDisabledSubmit"
            :loading="submitLoading"
            @clicked="onSubmit"
          />
        </div>
      </div>
    </div>

    <PreviousRecurringSchedulesModal
      v-if="previousOpened"
      :clientId="clientDataInput.id"
      @close-modal="previousOpened = false"
      @after-commit="fetchAvailbleCredits(clientDataInput.id)"
    />

    <BlBackdrop
      v-if="closeOpened"
      title="Are you sure you want to close?"
      description="You have unsaved progress in your recurring appointment creation. What would you like to do?"
      label="Confirm and close"
      :submitLoading="closeLoading"
      @backdrop-close="closeOpened = false"
      @backdrop-action="onCloseOverlay"
    >
      <div class="flex flex-col gap-2 px-8" :style="{ width: '40rem' }">
        <div
          class="flex flex-col items-center justify-center w-full gap-1 transition-all duration-300 border rounded-lg cursor-pointer h-22"
          :class="[
            closeProgress === 'discard'
              ? 'border-light-purple'
              : 'border-slate-400'
          ]"
          @click="closeProgress = 'discard'"
        >
          <BlTextSubtitle3
            text="Close and discard all progress"
            :class="{ 'text-light-purple': closeProgress === 'discard' }"
          />
          <BlTextBody3 text="Your appointment creation will be lost." />
        </div>
        <div
          class="flex flex-col items-center justify-center w-full gap-1 transition-all duration-300 border rounded-lg cursor-pointer h-22"
          :class="[
            closeProgress === 'save'
              ? 'border-light-purple'
              : 'border-slate-400'
          ]"
          @click="closeProgress = 'save'"
        >
          <BlTextSubtitle3
            text="Close and save your progress"
            :class="{ 'text-light-purple': closeProgress === 'save' }"
          />
          <div v-if="bookingAppointment">
            <BlTextBody3 text="You can resume the appointment creation until" />
            <BlTextBody3
              :text="
                displayDate({
                  date: bookingAppointment.end_time,
                  format: 'HH:mm'
                })
              "
            />
          </div>
        </div>
      </div>
    </BlBackdrop>

    <BlBackdrop
      v-if="backOpened"
      title="Are you sure you want to return to the previous step?"
      description="If you return to the previous step, all appointments generated in this step will be lost. Are you sure you want to go back?"
      label="Confirm"
      :submitLoading="closeLoading"
      @backdrop-close="backOpened = false"
      @backdrop-action="onBack"
      maxWidth="36rem"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import moment from "moment";

import SelectClientRecurring from "./SelectClientRecurring.vue";
import ManageScheduleRecurring from "./ManageScheduleRecurring.vue";
import HandleTimeslotRecurring from "./HandleTimeslotRecurring.vue";
import ReviewAppointmentRecurring from "./ReviewAppointmentRecurring.vue";
import PreviousRecurringSchedulesModal from "../modal/PreviousRecurringSchedulesModal.vue";

import BlTextTitle4 from "../../BlTextTitle4.vue";
import BlTextSubtitle1 from "../../BlTextSubtitle1.vue";
import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextSubtitle4 from "../../BlTextSubtitle4.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlTextBody3 from "../../BlTextBody3.vue";
import BlBreadcrumbs from "../../BlBreadcrumbs.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlInputToggle from "../../BlInputToggle.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import BlSearchBox from "../../BlSearchBox.vue";
import BlBackdrop from "../../BlBackdrop.vue";

export default {
  name: "NewRecurringAppointmentOverlay",
  components: {
    SelectClientRecurring,
    ManageScheduleRecurring,
    HandleTimeslotRecurring,
    ReviewAppointmentRecurring,
    PreviousRecurringSchedulesModal,
    //
    BlTextTitle4,
    BlTextSubtitle1,
    BlTextSubtitle3,
    BlTextSubtitle4,
    BlTextBody2,
    BlTextBody3,
    BlBreadcrumbs,
    BlIcon,
    BlInputButton,
    BlInputToggle,
    BlSelectBox,
    BlSearchBox,
    BlBackdrop
  },
  props: {
    availability: { type: Object },
    slots: { type: Array, default: () => [] },
    bookingId: { type: Number, default: 0 }
    // $emit('close-modal')
  },
  data() {
    const defaultClient = { id: 0, name: "No client selected" };
    return {
      defaultClient,
      timeslotView: "compact", // detailed | compact
      timeslotViewOptions: [
        {
          value: "detailed",
          label: "Detailed view",
          class: "font-bold text-light-purple"
        },
        {
          value: "compact",
          label: "Compact view",
          class: "font-bold text-light-purple"
        }
      ],
      //
      modalLoading: false,
      extendLoading: false,
      reviewLoading: false,
      submitLoading: false,
      closeLoading: false,
      currentSlot: null,
      //
      previousOpened: false,
      //
      counter: 10 * 60, // 10 minutes
      alertCountdown: 5 * 60, // 5 minutes
      showAlertCountdown: false,
      /** in_progress, expired */
      bookingStatus: "",
      /** select_client, manage_schedule, add_timeslot, edit_timeslot, review_appointment */
      process: "select_client",
      clientDataInput: defaultClient,
      //
      creditLoading: false,
      clientCreditsData: null,
      //
      editTimeslot: null,
      clientAppointments: [],
      recurrenceInput: {
        /** manage_schedule, review_appointment */
        process: "manage_schedule",
        interval: "1",
        /** based_on_available_credits, appointment_count */
        end_after: "",
        appointment_count: ""
      },
      backOpened: false,
      closeOpened: false,
      /** discard, save */
      closeProgress: "save",
      /** interval check status */
      intervalStatus: null
    };
  },
  computed: {
    ...mapGetters([
      "currentUser",
      "currentCenter",
      "bookingAppointment",
      "bookingTimeslots"
    ]),
    enableBranch() {
      return this.currentCenter.enable_branch;
    },
    breadcrumbs() {
      let currentLabel = "Open room";
      if (!this.isOpenRoom) {
        currentLabel = this.availability.user.name;
      }
      const action = () => this.onClickClose();
      return [
        { label: "Appointments", action },
        { label: currentLabel, action, truncate: true }
      ];
    },
    //
    isOpenRoom() {
      return !this.availability.user.id;
    },
    availableCredits() {
      if (!this.clientDataInput.id) return 0;
      if (!this.clientDataInput.credit_detail) return 0;
      const details = this.clientDataInput.credit_detail;
      const available = details.session_credits.available;
      const scheduled = details.scheduled_credits.total_credits || 0;
      return available - scheduled;
    },
    countdown() {
      let c = this.counter;
      if (c < 1) c = 0;

      let minutes = "00";
      let seconds = "00";
      seconds = (c % 60).toString().padStart(2, "0");
      if (c >= 60) {
        minutes = Math.floor(c / 60);
        if (c >= 3600) minutes = minutes % 60;
        minutes = minutes.toString().padStart(2, "0");
      }
      return minutes + ":" + seconds;
    },
    isDisabledReview() {
      if (!this.bookingTimeslots.length) return true;
      if (!this.recurrenceInput.end_after) return true;
      if (this.recurrenceInput.end_after === "appointment_count") {
        if (!this.recurrenceInput.appointment_count) return true;
      }

      let isConflicted = false;
      this.bookingTimeslots.forEach(i => {
        if (isConflicted) return true;
        const unavailableCredit =
          (i.booking_review_details &&
            i.booking_review_details.unavailable_credit) ||
          [];

        if (unavailableCredit.length) {
          let packageItemId = 0;
          if (i.credit_transaction_services.length) {
            packageItemId = i.credit_transaction_services[0].package_item_id;
          }
          if (unavailableCredit[0].package_item_id === packageItemId) {
            isConflicted = true;
          }
        }

        const unavailableTherapist =
          (i.booking_review_details &&
            i.booking_review_details.unavailable_therapist) ||
          [];
        const unavailableTherapistKeys = unavailableTherapist.map(i =>
          JSON.stringify(i)
        );

        const unavailableTimeslots =
          (i.booking_review_details &&
            i.booking_review_details.unavailable_timeslots) ||
          [];
        const unavailableTimeslotsKeys = unavailableTimeslots.map(i =>
          JSON.stringify(i)
        );

        // don't change object key order -- checking for similarity timeslots
        const currentSlotKey = JSON.stringify({
          date: i.date,
          room_id: i.room.id,
          user_id: (i.user && i.user.id) || 0,
          end_time: i.end_time_string,
          room_name: i.room.name,
          user_name: (i.user && i.user.name) || "",
          start_time: i.start_time_string
        });

        if (unavailableTherapistKeys.includes(currentSlotKey)) {
          isConflicted = true;
        }
        if (unavailableTimeslotsKeys.includes(currentSlotKey)) {
          isConflicted = true;
        }
      });
      return isConflicted;
    },
    minusCredit() {
      if (!this.bookingAppointment) return true;
      const uses = this.bookingAppointment.used_credit_transaction_services;
      if (!uses.length) return true;

      let minus = false;
      uses.forEach(i => {
        if (i.projected_remaining_credits < 0) minus = true;
      });
      return minus;
    },
    isDisabledSubmit() {
      const foundErrors = this.bookingTimeslots.find(i => i.is_conflicted);
      if (foundErrors) return true;

      if (this.minusCredit) return true;

      return false;
    }
  },
  async mounted() {
    if (this.bookingId) {
      const params = { id: this.bookingId };

      this.modalLoading = true;
      const { success, data } = await this.getBookingAppointment(params);
      this.modalLoading = false;
      if (!success) return;

      this.setupCounter(data);
      this.onChangeClient({ id: data.client_id });
    }
    if (!this.bookingId) {
      const slot = {
        key: `${this.availability.date}_${this.slots[0].start_time_string}_${this.availability.room.id}`,
        is_open_room: !this.availability.user.id,
        client_id: this.clientDataInput.id,
        client: this.clientDataInput,
        user_id: this.availability.user.id || 0,
        user: this.availability.user,
        room_id: this.availability.room.id,
        room: this.availability.room,
        date: this.availability.date,
        start_time_string: this.slots[0].start_time_string,
        end_time_string: this.slots[this.slots.length - 1].end_time_string,
        supervisor_ids: [],
        primary_color: this.availability.primary_color,
        secondary_color: this.availability.secondary_color
      };
      this.currentSlot = slot;
      this.process = "select_client";
    }
  },
  methods: {
    ...mapActions([
      "actionGet",
      "actionPost",
      "actionDelete",
      "getClient",
      "getCreditTransactionDetails",
      "getBookingAppointment",
      "createBookingAppointment",
      "extendTimeBookingAppointment",
      "updateBookingAppointment",
      "deleteBookingAppointment",
      "deleteBookingTimeslot",
      "createRecurringAppointments",
      "showToast"
    ]),
    displayDate,
    getSlotCount(start, end) {
      const s = start.split(":").map(i => Number(i));
      const e = end.split(":").map(i => Number(i));
      let slots = 0;
      slots += (e[0] - s[0]) * 2;
      if (s[1] === 30) slots -= 1;
      if (e[1] === 30) slots += 1;
      return slots;
    },
    setupCounter(booking) {
      this.counter = booking.remaining_time[0];
      this.bookingStatus = booking.status;
      if (this.bookingStatus === "in_progress") {
        if (this.counter <= this.alertCountdown) {
          this.showAlertCountdown = true;
        }
        this.counterTimer();
      }
      const slot = booking.appointments[0];
      this.currentSlot = {
        key: `${slot.date}_${slot.start_time_string}_${slot.room.id}`,
        ...slot
      };
      this.recurrenceInput = booking.recurrence_settings;
      this.process = booking.recurrence_settings.process || "manage_schedule";
    },
    counterTimer() {
      if (this.counter === this.alertCountdown) {
        this.showAlertCountdown = true;
      }
      if (this.counter >= 0) {
        setTimeout(() => {
          this.counter -= 1;
          this.counterTimer();
        }, 1000);
      }
      if (this.counter === -1) {
        this.bookingStatus = "expired";
      }
    },
    //
    async onChangeClient(client) {
      this.fetchAvailbleCredits(client.id);
      this.fetchAppointments(client.id);

      this.clientDataInput = this.defaultClient;
      const params = { id: client.id };
      const { success, data } = await this.getClient(params);
      if (!success) return;

      this.clientDataInput = data;
    },
    //
    async fetchAvailbleCredits(clientId) {
      this.clientCreditsData = null;
      if (!clientId) return;
      const params = { client_id: clientId };

      this.creditLoading = true;
      const { success, data } = await this.getCreditTransactionDetails(params);
      this.creditLoading = false;
      if (!success) return;

      this.clientCreditsData = data;
    },
    async fetchAppointments(clientId) {
      this.clientAppointments = [];
      if (!clientId) return;
      const params = {
        url: `/api/v1/appointments?client_id=${clientId}&status=scheduled,in_progress,completed`
      };

      const date = this.currentSlot.date || "";
      const start = moment(date).format("YYYY-MM-DD");
      const end = moment(date)
        .add(6, "days")
        .format("YYYY-MM-DD");
      params.url += `&start_date=${start}&end_date=${end}`;

      const { success, data } = await this.actionGet(params);
      if (!success) return;
      this.clientAppointments = data.appointments;
    },
    //
    async onCreateBooking(slot) {
      const params = {
        booking_appointment: {
          user_id: this.currentUser.id,
          client_id: this.clientDataInput.id,
          recurrence_settings: this.recurrenceInput
        },
        recurring_appointment: {
          client_id: this.clientDataInput.id,
          user_id: slot.user_id,
          room_id: slot.room_id,
          start_date: slot.date,
          start_time: slot.start_time_string,
          end_time: slot.end_time_string,
          supervisor_ids: slot.supervisor_ids,
          package_item_id: slot.package_item_id
        }
      };

      this.submitLoading = true;
      const { success, data } = await this.createBookingAppointment(params);
      this.submitLoading = false;
      if (!success) return;

      this.setupCounter(data);
      this.process = "manage_schedule";

      this.fetchAvailbleCredits(this.clientDataInput.id);
    },
    async onExtendCountdown() {
      const params = this.bookingAppointment;

      this.extendLoading = true;
      const { success, data } = await this.extendTimeBookingAppointment(params);
      this.extendLoading = false;
      if (!success) return;

      this.showAlertCountdown = false;
      this.counter = -10; // to remove recursive function before
      setTimeout(() => {
        this.setupCounter(data);
      }, 1000);
    },
    async onChangeRecurrance(recurrenceSetting) {
      const params = {
        id: this.bookingAppointment.id,
        recurrence_settings: recurrenceSetting
      };
      const { success } = await this.updateBookingAppointment(params);
      if (!success) return;

      this.recurrenceInput = recurrenceSetting;
      this.process = recurrenceSetting.process;
    },
    //
    openEditTimeslot(slot) {
      this.editTimeslot = slot;
      this.process = "edit_timeslot";
    },
    async onDeleteTimeslot(slot) {
      let params = { id: slot.recurring_appointment_id };
      await this.deleteBookingTimeslot(params);

      this.fetchAvailbleCredits(this.clientDataInput.id);

      params = { id: this.bookingAppointment.id };
      await this.getBookingAppointment(params);
    },
    async onDeleteReviewedTimeslot(slot) {
      let params = { id: slot.recurring_appointment_id };
      await this.deleteBookingTimeslot(params);

      this.fetchAvailbleCredits(this.clientDataInput.id);

      params = { id: this.bookingAppointment.id };
      await this.getBookingAppointment(params);
    },
    //
    async onBack() {
      const params = {
        url: `/api/v1/booking_appointments/${this.bookingAppointment.id}/delete_in_review_appointments`
      };

      this.closeLoading = true;
      const { success } = await this.actionDelete(params);
      this.closeLoading = false;
      if (!success) return;

      const recurrenceSetting = {
        ...this.recurrenceInput,
        process: "manage_schedule"
      };
      this.onChangeRecurrance(recurrenceSetting);
      this.fetchAppointments(this.clientDataInput.id);
      this.backOpened = false;
    },
    async onClickClose() {
      if (this.process === "select_client") {
        this.$emit("close-modal");
      }
      if (this.bookingStatus === "completed") {
        this.$emit("close-modal");
      }
      if (this.bookingStatus === "expired") {
        const params = this.bookingAppointment;
        this.closeLoading = true;
        await this.deleteBookingAppointment(params);
        this.closeLoading = false;
        this.$emit("close-modal");
      }
      if (this.bookingStatus === "in_progress") {
        this.closeOpened = true;
      }
    },
    async onCloseOverlay() {
      if (this.closeProgress === "discard" && this.bookingAppointment) {
        const params = this.bookingAppointment;
        this.closeLoading = true;
        await this.deleteBookingAppointment(params);
        this.closeLoading = false;
        this.$emit("close-modal");
      } else {
        this.$emit("close-modal");
      }
    },
    //
    async onReview() {
      let arr = this.bookingTimeslots.map(i => {
        const day = moment(i.date)
          .format("dddd")
          .toLowerCase();
        const serviceId = i.credit_transaction_services.length
          ? i.credit_transaction_services[0].package_item_id
          : null;
        return {
          ...i,
          client_id: i.client.id,
          user_id: i.user.id,
          room_id: i.room.id,
          start_date: i.date,
          start_time: i.start_time_string,
          end_time: i.end_time_string,
          supervisor_ids: i.supervisors.map(i => i.id),
          package_item_id: serviceId,
          //
          interval: this.recurrenceInput.interval,
          repetition_type: "week",
          days_of_week: [day],
          occurrences: 0
        };
      });

      /** based_on_available_credits, appointment_count */
      if (this.recurrenceInput.end_after === "based_on_available_credits") {
        let credits = this.availableCredits;
        let available = true;
        while (available) {
          arr = arr.map(i => {
            const creditsUsed = this.getSlotCount(i.start_time, i.end_time);
            let occurrences = i.occurrences;
            if (credits - creditsUsed >= 0) {
              credits -= creditsUsed;
              occurrences += 1;
            } else available = false;
            return { ...i, occurrences };
          });
        }
        arr = arr.filter(i => i.occurrences > 0); // to filter if there no occurances
      }
      if (this.recurrenceInput.end_after === "appointment_count") {
        let count = Math.max(
          this.recurrenceInput.appointment_count,
          arr.length
        );
        while (count !== 0) {
          arr = arr.map(i => {
            let occurrences = i.occurrences;
            if (count - 1 >= 0) {
              count -= 1;
              occurrences += 1;
            }
            return { ...i, occurrences };
          });
        }
      }

      const params = {
        params: {
          booking_appointment_id: this.bookingAppointment.id,
          booking_in_review: true,
          data_appointments: arr
        },
        title: null,
        message: null,
        hideToast: true
      };

      this.reviewLoading = true;
      await this.createRecurringAppointments(params);
      this.reviewLoading = false;

      const recurrenceSetting = {
        ...this.recurrenceInput,
        process: "review_appointment"
      };
      this.onChangeRecurrance(recurrenceSetting);
    },
    async onSubmit() {
      const arr = this.bookingTimeslots.map(i => {
        const packageItemId = i.credit_transaction_services.length
          ? i.credit_transaction_services[0].package_item_id
          : null;
        return {
          client_id: i.client.id,
          user_id: i.user.id,
          room_id: i.room.id,
          start_date: i.date,
          start_time: i.start_time_string,
          end_time: i.end_time_string,
          supervisor_ids: i.supervisors.map(i => i.id),
          package_item_id: packageItemId
        };
      });
      const params = {
        params: {
          booking_appointment_id: this.bookingAppointment.id,
          data_appointments: arr
        },
        title: null,
        message: `Success! <b>${arr.length}</b> appointment(s) for <b>${this.clientDataInput.name}</b> has been added.`
      };

      this.submitLoading = true;
      this.onStartIntervalStatus();
      const { success } = await this.createRecurringAppointments(params);
      if (!success) {
        this.submitLoading = false;
        return;
      }
      setTimeout(() => {
        this.submitLoading = false;
        this.$emit("close-modal");
      }, 1000);
    },
    async onStartIntervalStatus() {
      const params = { id: this.bookingAppointment.id };
      const { success, data } = await this.getBookingAppointment(params);
      if (success) {
        this.counter = data.remaining_time[0];
        this.bookingStatus = data.status;
        if (data.status === "completed") {
          const title = "New appointment added";
          const message = `Success! appointments have been added.`;
          this.showToast({ title, message, type: "success" });
          setTimeout(() => {
            this.submitLoading = false;
            this.$emit("close-modal");
            if (this.intervalStatus) {
              clearInterval(this.intervalStatus);
              this.intervalStatus = null;
            }
          }, 1000);
        }
      }
      if (!this.intervalStatus) {
        this.intervalStatus = setInterval(async () => {
          this.onStartIntervalStatus();
        }, 5000);
      }
    },
    async onRefresh() {
      const params = { id: this.bookingAppointment.id };
      this.extendLoading = true;
      await this.getBookingAppointment(params);
      this.extendLoading = false;
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/appointments/overlay/NewRecurringAppointmentOverlay.vue